import { normalizeRole } from '../../core/rbac/permissions';

const ROLE_LABELS: Readonly<Record<string, string>> = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  gam: 'Sales Account Owner',
  saleshead: 'CRM Head',
  csm: 'CSM',
  cxlead: 'Campaign Designer',
  cxcsmhead: 'CX & CSM Head',
  techlead: 'Technical Lead',
  billinglead: 'Billing Lead',
  supportlead: 'Support Lead',
};

export const TEAM_GROUP_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'carrier', label: 'Carrier' },
  { value: 'credit-billing-settlement', label: 'Credit, Billing & Settlement' },
  { value: 'customer-experience', label: 'Customer Experience (CX)' },
  { value: 'customer-success', label: 'Customer Success (CSM)' },
  { value: 'finance', label: 'Finance Team' },
  { value: 'global-enterprise', label: 'Global Enterprise' },
  { value: 'global-enterprise-hyperscaler', label: 'Global Enterprise & Hyperscaler' },
  { value: 'hyperscaler', label: 'Hyperscaler' },
  { value: 'network-operations-support', label: 'Network Operations Support' },
  { value: 'service-desk', label: 'Service Desk' },
  { value: 'system-admin', label: 'System Admin' },
] as const;

/** Canonical labels plus aliases for previously stored / DWH values. */
const GROUP_LABELS: Readonly<Record<string, string>> = {
  ...Object.fromEntries(TEAM_GROUP_OPTIONS.map((opt) => [opt.value, opt.label])),
  enterprise: 'Global Enterprise',
  management: 'Management',
};

const KNOWN_GROUP_KEYS = new Set(Object.keys(GROUP_LABELS));
const KNOWN_ROLE_KEYS = new Set(Object.keys(ROLE_LABELS));
const GROUP_LABEL_TO_VALUE = new Map(
  [
    ...TEAM_GROUP_OPTIONS.map((opt) => [opt.label.toLowerCase(), opt.value] as const),
    ['enterprise', 'global-enterprise'] as const,
    ['management', 'management'] as const,
  ],
);

export const ENTERPRISE_ROLE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'cxlead', label: 'Campaign Designer' },
  { value: 'gam', label: 'Sales Account Owner' },
  { value: 'saleshead', label: 'CRM Head' },
  { value: 'csm', label: 'CSM' },
  { value: 'techlead', label: 'Technical Lead' },
  { value: 'cxcsmhead', label: 'CX & CSM Head' },
  { value: 'billinglead', label: 'Billing Lead' },
  { value: 'supportlead', label: 'Support Lead' },
] as const;

export function looksLikeEmail(value: string | null | undefined): boolean {
  const v = value?.trim() || '';
  return !!v && v.includes('@');
}

export function formatRoleLabel(role: string | null | undefined): string {
  const key = normalizeRole(role);
  if (!key) return '—';
  // SSO sometimes puts team group in the role field — don't label it as a role.
  if (KNOWN_GROUP_KEYS.has(key) && !KNOWN_ROLE_KEYS.has(key)) {
    return '—';
  }
  return ROLE_LABELS[key] || titleCaseToken(role!.trim());
}

/** Normalize API / DWH / form values to a catalog slug when possible. */
export function toTeamGroupValue(group: string | null | undefined): string {
  const raw = group?.trim() || '';
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (GROUP_LABELS[lower]) {
    if (lower === 'enterprise') return 'global-enterprise';
    return lower;
  }
  return GROUP_LABEL_TO_VALUE.get(lower) || lower;
}

export function formatGroupLabel(group: string | null | undefined): string {
  const key = toTeamGroupValue(group);
  if (!key) return '—';
  return GROUP_LABELS[key] || titleCaseToken(group!.trim());
}

/**
 * SSO callbacks sometimes send `group` (e.g. enterprise) in the `role` query param.
 * Split so the topbar does not show "Enterprise" as the platform role.
 */
export function coerceRoleAndGroup(
  role?: string | null,
  group?: string | null,
): { role: string; group?: string } {
  const rawRole = role?.trim() || '';
  const rawGroup = group?.trim() || '';
  const roleKey = normalizeRole(rawRole);
  const groupKey = rawGroup.toLowerCase();

  if (rawRole && KNOWN_GROUP_KEYS.has(roleKey) && !KNOWN_ROLE_KEYS.has(roleKey)) {
    return {
      role: KNOWN_ROLE_KEYS.has(groupKey) ? rawGroup : '',
      group: toTeamGroupValue(rawRole || rawGroup) || undefined,
    };
  }

  return {
    role: roleKey || rawRole,
    group: toTeamGroupValue(rawGroup) || undefined,
  };
}

export function resolvePersonName(input: {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  email?: string | null;
}): { firstName: string; lastName: string; fullName: string } {
  const email = input.email?.trim() || '';
  let first = cleanNamePart(input.firstName, email);
  let last = cleanNamePart(input.lastName, email);

  if (!first && !last) {
    const fromFull = splitDisplayName(input.fullName, email);
    first = fromFull.first;
    last = fromFull.last;
  }

  // Still empty — derive a readable label from the email local-part.
  if (!first && !last && email) {
    const local = email.split('@')[0] || '';
    const tokens = local
      .split(/[._-]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map(titleCaseToken);
    if (tokens.length >= 2) {
      first = tokens[0]!;
      last = tokens.slice(1).join(' ');
    } else if (tokens.length === 1) {
      first = tokens[0]!;
    }
  }

  const fullName = `${first} ${last}`.trim() || email || 'User';
  return {
    firstName: first || '—',
    lastName: last || '—',
    fullName,
  };
}

function cleanNamePart(value: string | null | undefined, email: string): string {
  const v = value?.trim() || '';
  if (!v) return '';
  if (looksLikeEmail(v)) return '';
  if (email && v.toLowerCase() === email.toLowerCase()) return '';
  return v;
}

function splitDisplayName(
  fullName: string | null | undefined,
  email: string,
): { first: string; last: string } {
  const raw = fullName?.trim() || '';
  if (!raw || looksLikeEmail(raw) || (email && raw.toLowerCase() === email.toLowerCase())) {
    return { first: '', last: '' };
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  if (!parts.length) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0]!, last: '' };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}

function titleCaseToken(value: string): string {
  if (!value) return value;
  if (/^[A-Z0-9_]+$/.test(value) && value.includes('_')) {
    return value
      .toLowerCase()
      .split('_')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}
